import React, { memo, useContext, useEffect, useMemo, useRef, useState } from "react";
import { VideoStream } from "../types/videos";
import blackImage from '../../images/black-image.jpeg';
import { useSelector } from "react-redux";
import { GreenroomState as GS } from '../../stores/types/types';
import { appLogger } from '../../utils/datadog';
import classNames from "classnames";
import { AVContext } from "../../contexts/audio-video";
import { contrastingColor } from "../../utils/utils";
import { cleanupVideoStreamElement } from "../../utils/video/video-streams";
import useShotMemos from "../../hooks/use-shot-memos";
import { ROOM_STATES } from "../../stores/reducers/settings";
import styled from "styled-components";

const VideoContainer = styled.div`
	height: 100%;
	width: 100%;
	display: flex;
	justify-content: center;
`;

const ExpandContainer = styled.div`
	height: 100%;

	&.wide {
		height: auto;
		width: 100%;
	}

	&.expanded {
		width: auto;
		aspect-ratio: 16/9;
	}
`;

const ASPECT_RATIO_4_3 = 4 / 3;

interface Props {
	feedPosition: number;
	mediaId: string;
}

const FeedSipCallSpotlight = (props: Props) => {
	const {
		feedPosition,
        mediaId,
	} = props;

	const connectedPresenterVideos = useSelector(
		(state: GS) => state.VideoReducer.connectedPresenterVideos,
	);
	const meeting = useSelector((s: GS) => s.SessionReducer.meeting);
	const embed = useSelector((s: GS) => s.SessionReducer.embed);
	const backstageCanvasVolume = useSelector((s: GS) => s.MeetingReducer.backstageCanvasVolume);
	const roomState = useSelector((s: GS) => s.MeetingReducer.roomState);
	const [isWideView, setIsWideView] = useState<boolean>(false);

	const videoElRef = useRef<HTMLVideoElement>(null);

    const video = connectedPresenterVideos.find(
        (vid: VideoStream) => vid.isSipCall && vid.id === mediaId,
    );

	const { getAV } = useContext(AVContext);
	const { video: hasVideo } = getAV(video?.id);

	const { currentShot, isOnCanvas } = useShotMemos();

	const shotFeed = currentShot.shot_data.shotFeeds.find(shotFeed => shotFeed.sipStreamId === mediaId);
	const expanded = shotFeed?.expanded ?? false;

	const colorStyles = useMemo(() => {
		const colorObj = {
			backgroundColor: '#333',
			color: contrastingColor('#333333'),
		};

		if (meeting?.primary_color) {
			colorObj.backgroundColor = `#${meeting.primary_color}`;
			colorObj.color = contrastingColor(meeting.primary_color);
		}

		return colorObj;
	}, [meeting]);

	async function playPresenterFeed(srcObject: MediaStream) {
		if (!videoElRef.current) return;

		videoElRef.current.srcObject = srcObject;
		videoElRef.current.volume = 0;
		if (!srcObject.active) return;

		try {
			await videoElRef.current.play();
			if (videoElRef.current?.paused) {
				try {
					await videoElRef.current.play();
				} catch (e: any) {
					appLogger.error(
						'video el play request error',
						{ location: 'feed-presenter second' },
						e,
					);
				}
			}
		} catch (e: any) {
			appLogger.error(
				'video el play request error',
				{ location: 'feed-presenter first' },
				e,
			);
		}

		cleanupVideoStreamElement(videoElRef.current);
	}

	const videoDebounce = useRef<NodeJS.Timeout | null>(null);
	const videoContainerRef = useRef<HTMLDivElement>(null);

	// This attaches connectedPresenterVideo.srcObject to our video DOM element.
	// The srcObject is derived from OpenTok's publisher/subscriber object.
	// This has been known to cause issues where the user's video does not appear.
	// (Presenter appears as a black feed instead.)
	// green-4902
	useEffect(() => {
		if (videoElRef.current && video && video?.srcObject !== videoElRef.current.srcObject) {
			if (videoDebounce.current) { clearTimeout(videoDebounce.current); }
			videoDebounce.current = setTimeout(() => {
				playPresenterFeed(video.srcObject);
			}, 100);
		}
	}, [video?.srcObject, videoElRef.current]);

	useEffect(() => {
		if (videoElRef.current) {
			videoElRef.current.volume = (
				(isOnCanvas || embed || roomState === ROOM_STATES.EDITING)
					? 1
					: backstageCanvasVolume
			);
		}
	}, [isOnCanvas, roomState, backstageCanvasVolume]);

	useEffect(() => {
		if (videoContainerRef.current) {
			const rect = videoContainerRef.current.getBoundingClientRect();
			setIsWideView((rect.width / rect.height) > ASPECT_RATIO_4_3);
		}
	}, []);

	return (
		<VideoContainer ref={videoContainerRef}>
			{video && video.srcObject && video?.srcObject.active
				? <>
					<ExpandContainer className={classNames(isWideView && 'wide', expanded && 'expanded')}>
						<video
							key={video.srcObject.id + feedPosition}
							muted={true}
							poster={blackImage}
							autoPlay={true}
							playsInline
							ref={videoElRef}
							onLoadedData={() => {
								// Force play on presenter video elements to avoid a
								// paused OpenTok publisher/subscriber feed.
								// green-4902
								videoElRef.current?.play().catch((e) => {
									appLogger.error('video el play request error', {
										location: 'feed-presenter third',
									}, e);
								});
							}}
						>
						</video>
					</ExpandContainer>

					<div className={classNames('poster', { active: !hasVideo })}>
						{video.poster 
							? <img className="poster-image" src={video.poster} alt={''} />
							: <div className="no-image-placeholder">
								<span style={colorStyles}>
									{video.displayName}
								</span>
							</div>
						}
					</div>

				</>
				: <>
					<div className={classNames('poster active')}>
						{!!video?.poster
							? <img className="poster-image" src={video.poster} alt={''} />
							: <div className="no-image-placeholder">
								<span style={colorStyles}>{video?.displayName}</span>
							</div>
						}
					</div>
				</>
			}
		</VideoContainer>
    );
}

export default memo(FeedSipCallSpotlight);
